import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "saipass_install_dismissed";
const DISMISS_DAYS = 7;

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return iOS && isSafari;
}

function recentlyDismissed() {
  const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
  if (!ts) return false;
  return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export default function InstallPrompt() {
  const location = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [mode, setMode] = useState(null); // 'android' | 'ios'
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode("android");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    if (isIOS()) setMode("ios");

    const t = setTimeout(() => {
      setMode((m) => {
        if (m) setVisible(true);
        return m;
      });
    }, 12000);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (mode === "android" && deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
      dismiss();
    }
  };

  // Never show on the admin panel.
  if (location.pathname.startsWith("/admin")) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 300 }}
          className="fixed bottom-24 left-1/2 z-[55] w-[calc(100%-2rem)] max-w-[26rem] -translate-x-1/2 overflow-hidden rounded-2xl border border-black/5 bg-white shadow-[0_12px_40px_rgba(0,0,0,0.16)]"
          data-testid="install-prompt"
        >
          <div className="flex items-start gap-3 p-4">
            <img src="/icon-192.png" alt="SAIPASS Beneficios" className="h-11 w-11 shrink-0 rounded-xl border border-black/5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold leading-snug text-[#1A1A1A]">
                Ten a la mano el Directorio de Negocios Locales
              </p>
              {mode === "ios" ? (
                <p className="mt-1 flex flex-wrap items-center gap-1 text-xs leading-relaxed text-[#5C5C5C]">
                  Toca <Share size={13} className="inline text-[#E54D2E]" /> y luego
                  <span className="inline-flex items-center gap-0.5 font-semibold text-[#1A1A1A]">
                    <Plus size={12} /> Agregar a inicio
                  </span>
                  para instalar SAIPASS Beneficios.
                </p>
              ) : (
                <p className="mt-1 text-xs leading-relaxed text-[#5C5C5C]">
                  Instala SAIPASS Beneficios y accede con un solo toque desde la pantalla de inicio de tu teléfono.
                </p>
              )}
            </div>
            <button onClick={dismiss} data-testid="install-dismiss" className="rounded-full p-1 text-[#9c9c9c] hover:bg-[#F3F0E6]">
              <X size={18} />
            </button>
          </div>
          {mode === "android" && (
            <div className="flex gap-2 px-4 pb-4">
              <button onClick={dismiss} data-testid="install-later" className="flex-1 rounded-xl bg-[#F3F0E6] py-2.5 text-sm font-bold text-[#1A1A1A]">
                Ahora no
              </button>
              <button onClick={install} data-testid="install-accept" className="flex flex-[2] items-center justify-center gap-2 rounded-xl bg-[#E54D2E] py-2.5 text-sm font-bold text-white">
                <Download size={16} /> Instalar app
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
